<script setup>
import { computed } from 'vue';

/**
 * Marcador de origem do lead: de onde a pessoa veio, numa olhada.
 *
 * Le o atributo personalizado que o Leads::OriginWriter carimba no contato.
 * Nao busca nada: o contato ja viaja no payload da conversa, entao o chip nao
 * custa requisicao nenhuma. Sem origem carimbada, nao aparece (a conta pode
 * nem ter a funcao ligada, e chip vazio e pior que chip nenhum).
 */
const props = defineProps({
  contact: { type: Object, default: () => ({}) },
  // V3 (mockup final-v3v2.html): na linha de identidade a origem e TEXTO ao
  // lado do nome da caixa, nao uma pilula solta. Pilula ali competiria com as
  // acoes; origem qualifica QUEM e a pessoa, entao mora grudada no nome.
  inline: { type: Boolean, default: false },
});

// Cada gaveta com o rotulo que o atendente entende e um icone que se distingue
// de longe. As cores seguem os tokens do tema, nunca cor solta.
const GAVETAS = {
  meta_ads: {
    rotulo: 'Anúncio Meta',
    icone: 'i-ri-meta-fill',
    cor: 'text-n-blue-11',
  },
  google_ads: {
    rotulo: 'Anúncio Google',
    icone: 'i-ri-google-fill',
    cor: 'text-n-amber-11',
  },
  instagram_organico: {
    rotulo: 'Instagram',
    icone: 'i-ri-instagram-line',
    cor: 'text-n-ruby-11',
  },
  whatsapp_direto: {
    rotulo: 'WhatsApp direto',
    icone: 'i-ri-whatsapp-line',
    cor: 'text-n-teal-11',
  },
  site: { rotulo: 'Site', icone: 'i-ri-global-line', cor: 'text-n-slate-11' },
  evento: {
    rotulo: 'Evento',
    icone: 'i-ri-calendar-event-line',
    cor: 'text-n-iris-11',
  },
  campanha: {
    rotulo: 'Campanha',
    icone: 'i-ri-megaphone-line',
    cor: 'text-n-iris-11',
  },
  email: { rotulo: 'E-mail', icone: 'i-ri-mail-line', cor: 'text-n-slate-11' },
  youtube: {
    rotulo: 'YouTube',
    icone: 'i-ri-youtube-line',
    cor: 'text-n-ruby-11',
  },
  comercial: {
    rotulo: 'Time comercial',
    icone: 'i-ri-user-star-line',
    cor: 'text-n-slate-11',
  },
  indicacao: {
    rotulo: 'Indicação',
    icone: 'i-ri-user-shared-line',
    cor: 'text-n-teal-11',
  },
  outro: {
    rotulo: 'Origem não identificada',
    icone: 'i-ri-question-line',
    cor: 'text-n-slate-10',
  },
};

// Anuncio pago leva o icone da plataforma junto do texto: e a informacao que
// muda a conversa de venda. Origem organica vai so em texto, discreta.
const PAGAS = ['meta_ads', 'google_ads'];

const atributos = computed(() => props.contact?.custom_attributes || {});
const gaveta = computed(() => atributos.value.origem_lead);
const detalhe = computed(() => atributos.value.origem_lead_detalhe);
const apresentacao = computed(() => GAVETAS[gaveta.value] || null);
const paga = computed(() => PAGAS.includes(gaveta.value));
const textoLinha = computed(() => {
  if (!apresentacao.value) return '';
  return detalhe.value
    ? `${apresentacao.value.rotulo} · ${detalhe.value}`
    : apresentacao.value.rotulo;
});

const dica = computed(() => {
  if (!apresentacao.value) return '';
  const base = `Origem: ${apresentacao.value.rotulo}`;
  return detalhe.value ? `${base} · ${detalhe.value}` : base;
});
</script>

<template>
  <span
    v-if="apresentacao && inline"
    v-tooltip.bottom="dica"
    data-testid="lead-origin-inline"
    class="inline-flex min-w-0 items-center gap-1 truncate text-[11.5px]"
    :class="paga ? 'text-n-blue-11' : 'text-n-teal-11'"
  >
    <span
      v-if="paga"
      :class="apresentacao.icone"
      class="size-3 shrink-0"
      aria-hidden="true"
    />
    <span class="truncate">{{ textoLinha }}</span>
  </span>
  <span
    v-else-if="apresentacao"
    v-tooltip.bottom="dica"
    data-testid="lead-origin-chip"
    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-n-alpha-2 text-xs font-medium shrink-0 max-w-40"
    :aria-label="dica"
  >
    <span
      :class="[apresentacao.icone, apresentacao.cor]"
      class="size-3 shrink-0"
    />
    <span class="truncate text-n-slate-12">{{ apresentacao.rotulo }}</span>
  </span>
</template>
